/* eslint-disable no-underscore-dangle */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { QueryParamKey, AccountActivation, WEBREQ_ADMIN_USER_TYPES, BOOKING_ADMIN_USER_TYPES } from 'src/app/appsettings';
import { ELoginMethod } from 'src/app/plasma-ui-common/enums/login-method.enum';
import { UserTypes } from 'src/app/plasma-ui-common/enums/user-type.enum';
import { EAuthenticationType } from 'src/app/component-modules/registerv2/register-mfa-sms/register-mfa-sms.component';
import { ApiService } from 'src/app/services/api/api.service';
import {
  generalScopeKey,
  registrationSecureScopeKey,
  twoFactorAuthenticatorScopeKey,
} from 'src/app/services/i18n/i18n.service';
import { RegisterActions } from '../actions/registration.actions';
import { AppState } from '../reducers';
import { IClaim } from '../reducers/registration.reducer';
import { StsSelectors } from '../selectors/sts.selectors';
import { getErrorMessage } from 'src/app/utils/error.util';
import { RegisterProfileApiService } from 'src/app/services/register-profile-api/register-profile-api.service';
import { RegistrationSelectors } from '../selectors/registration.selectors';
import { ConfigService } from 'src/app/services/config/config.service';
import { EStorageKeys } from 'src/app/models/storage.model';
import { isMobileAppReturnUrl } from 'src/app/utils/is-mobile-app-return-url';
import { withCampaignParams } from 'src/app/services/campaign-params.service';

const translationKey = 'taraEmailNotificationComponent';

@Injectable()
export class RegistrationEffects {
  bookingUrl$ = this._store.select(StsSelectors.getBaseBookingUrl);
  plasmaUrl$ = this._store.select(StsSelectors.getBasePlasmaUrl);
  baseAuthUrl$ = this._store.select(StsSelectors.baseAuthUrl);
  bookingAdminUrl$ = this._store.select(StsSelectors.getBookingAdminUrl);
  adminUrl$ = this._store.select(StsSelectors.getAdminUrl);
  webreqAdminUrl$ = this._store.select(StsSelectors.getWebreqAdminUrl);
  isCZTenant$ = this._store.select(StsSelectors.isCzechRepublic);
  isBETenant$ = this._store.select(StsSelectors.isBETenant);
  isFITenant$ = this._store.select(StsSelectors.isFITenant);
  isUKTenant$ = this._store.select(StsSelectors.isUkDfpInstance);
  isPatient$ = this._store.select(RegistrationSelectors.getRegistrationIsPatient);
  registrationUserType$ = this._store.select(RegistrationSelectors.getRegistrationUserType);
  isForBooking$ = this._activatedRoute.queryParamMap.pipe(
    map(x => {
      const isForBookingQuery = x.get('isForBooking');
      return (
        typeof isForBookingQuery === 'string' && isForBookingQuery.trim().toLowerCase() === 'true'
      );
    }),
  );
  paramMap$ = this._activatedRoute.queryParamMap;
  userId$ = this.paramMap$.pipe(map(x => x.get(QueryParamKey.USER_ID)));

  constructor(
    private readonly _actions$: Actions,
    private readonly _api: ApiService,
    private readonly _store: Store<AppState>,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _transloco: TranslocoService,
    private readonly _toastr: ToastrService,
    private readonly configService: ConfigService,
    private readonly registerProfileApi: RegisterProfileApiService,
  ) {}

  loadRegistrationClaims$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.loadRegistrationClaims),
        switchMap(({ id, seqId }) => {
          return this._api.get('/manage/accounts/complete_profile', {
            id,
            seqId,
            sequenceId: seqId,
          });
        }),
        tap(n => {
          const claims = <any>this.getClaimsAsObject(n);
          this._store.dispatch(RegisterActions.loadRegistrationClaimSuccess({ claims }));
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.loadRegistrationClaimFailed({ error }));
          return of(null);
        }),
      );
    },
    { dispatch: false },
  );

  loadExistsRegistrationClaims$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.loadExistingUserClaims),
        switchMap(action => this.registerProfileApi.getPtExisitngProfile(action.userid)),
        tap(n => {
          const claims = <any>this.getClaimsAsObject(n);
          this._store.dispatch(RegisterActions.loadRegistrationClaimSuccess({ claims }));
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.loadRegistrationClaimFailed({ error }));
          return of(null);
        }),
      );
    },
    { dispatch: false },
  );

  loadExternalAuthProviders$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.loadExternalAuthProviders),
        switchMap(() => this._api.get('/manage/externalauthproviders')),
        tap(items => {
          this._store.dispatch(RegisterActions.loadExternalAuthProviderSuccess({ items }));
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.loadExternalAuthProviderFailed({ error }));
          return of(null);
        }),
      );
    },
    { dispatch: false },
  );

  setUserPassword$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.setUserPassword),
        switchMap(({ payload, regId }) =>
          this._api
            .put(`/manage/accounts/set_authentication_provider?id=${regId}`, payload)
            .pipe(map(() => regId)),
        ),
        tap(id => this.handleRedirection(id)),
        catchError(error => {
          this._store.dispatch(RegisterActions.setUserPasswordFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  handleRedirection(id: string): void {
    this.bookingUrl$
      .pipe(
        withLatestFrom(
          this.isPatient$,
          this.isBETenant$,
          this.isCZTenant$,
          this.isFITenant$,
          this.isUKTenant$,
          this.plasmaUrl$,
          this.bookingAdminUrl$,
          this.webreqAdminUrl$,
          this.adminUrl$,
          this.registrationUserType$,
          this.baseAuthUrl$,
        ),
        take(1),
      )
      .subscribe(
        ([
          bookingUrl,
          isPatient,
          isBe,
          isCZ,
          isFi,
          isUK,
          basePlasmaUrl,
          bookingAdminUrl,
          webreqAdminUrl,
          adminUrl,
          userType,
          baseAuthUrl,
        ]) => {
          this.redirectUser({
            id,
            bookingUrl,
            isPatient,
            isBe,
            isCZ,
            isFi,
            isUK,
            basePlasmaUrl,
            bookingAdminUrl,
            webreqAdminUrl,
            adminUrl,
            userType,
            baseAuthUrl,
          });
        },
      );
  }

  redirectUser(params: {
    id: string;
    bookingUrl: string;
    isPatient: boolean;
    isBe: boolean;
    isCZ: boolean;
    isFi: boolean;
    isUK: boolean;
    basePlasmaUrl: string;
    bookingAdminUrl: string;
    webreqAdminUrl: string;
    adminUrl: string;
    userType: string | string[];
    baseAuthUrl: string;
  }): void {
    const {
      id,
      bookingUrl,
      isPatient,
      isBe,
      isCZ,
      isFi,
      isUK,
      basePlasmaUrl,
      bookingAdminUrl,
      webreqAdminUrl,
      adminUrl,
      userType,
      baseAuthUrl,
    } = params;

    // SA-21781: the secure_account step (password + MFA setup, just completed above this call)
    // consumed nothing, so a SYNLAB Mobile app WebView user landed on one of the portals below
    // with their OIDC login still pending. Per the updated ticket scope, we don't auto-resume
    // that request - doing so would require forwarding the stored return url as a redirect
    // target, which is itself an open-redirect surface (flagged in SA-21781's PR review). Instead,
    // tell the user to verify and sign back in, and send them to the STS's plain login page -
    // no return url, so there's nothing here for a crafted value to redirect to.
    // Gated on the stored return url carrying the mobile app's exact client_id, so every other
    // tenant and flow keeps the redirects below byte for byte.
    //
    // Cleared unconditionally (not just on the mobile branch below): RegistrationGuard stores
    // this on every complete-registration visit, but secure_account can also be reached other
    // ways that don't go through that guard (BankID, complete-profile.component.ts). Without
    // this, a stale value from an abandoned mobile flow would survive in storage and wrongly
    // match a later, unrelated registration that reaches this method by one of those other
    // routes (flagged in SA-21781's PR review).
    const pendingReturnUrl: string | null = localStorage.getItem(
      EStorageKeys.REGISTER_REDIRECT_URL,
    );
    localStorage.removeItem(EStorageKeys.REGISTER_REDIRECT_URL);
    if (isMobileAppReturnUrl(pendingReturnUrl)) {
      const title = this.translate(generalScopeKey, 'successTitle');
      const message = this.translate(
        registrationSecureScopeKey,
        'mobileAppVerifyAndLoginAgainText',
      );
      this._toastr.success(message, title);

      // Give the user a moment to read the toast before the full-page navigation away from
      // this SPA unmounts it.
      setTimeout(() => {
        window.location.href = `${baseAuthUrl}/Account/Login`;
      }, 3000);
      return;
    }

    let arrUserType: string[] = Array.isArray(userType) ? userType : userType?.split(',');
    arrUserType = arrUserType ?? [];

    const isWebreqAdmin = this.checkIfRoleExists(arrUserType, WEBREQ_ADMIN_USER_TYPES);
    const isBookingAdmin = this.checkIfRoleExists(arrUserType, BOOKING_ADMIN_USER_TYPES);
    const isFIAdmin = this.checkIfRoleExists(
      arrUserType,
      [UserTypes.SYNLAB_ACCESS_USER, UserTypes.DOCTOR],
      true,
    );

    const activeLanguage = this._transloco.getActiveLang();

    if (isCZ || isBe) {
      this.leaveFor(`${basePlasmaUrl}?langCode=${activeLanguage}`);
    } else if (isFi && isBookingAdmin) {
      this.leaveFor(bookingAdminUrl);
    } else if (isFi && isWebreqAdmin) {
      this.leaveFor(webreqAdminUrl);
    } else if (isFi && isFIAdmin) {
      this.leaveFor(adminUrl);
    } else if (isUK) {
      this.leaveFor(`${this.configService._envConfig?.BaseBookingUrl}?openbooking=true`);
    } else if (bookingUrl && isPatient) {
      this.handlePatientRedirection(bookingUrl);
    } else {
      // built before the storage is cleared below - the campaign parameters live there
      const loginUrl = withCampaignParams(baseAuthUrl, this.configService._envConfig);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = loginUrl;
      // this._router.navigate(['registration', 'success'], { queryParams: { id } });
    }
  }

  /** A full-page navigation to the STS or another of our apps that keeps the campaign parameters (SA-21082). */
  private leaveFor(url: string): void {
    window.location.href = withCampaignParams(url, this.configService._envConfig);
  }

  checkIfRoleExists(userRoles: string[], validRoles: string[], negate = false): boolean {
    const roleMatches = userRoles.filter(role => validRoles.includes(role as any)).length > 0;
    return negate ? !roleMatches : roleMatches;
  }

  handlePatientRedirection(bookingUrl: string): void {
    let isTara = false;
    this._store
      .select('registerProfile')
      .pipe(take(1))
      .subscribe(registerProfile => {
        registerProfile.registerProfile.forEach(claim => {
          if (
            claim?.claimType === 'authentication_provider' &&
            claim?.claimValue?.toLowerCase() === 'tara'
          ) {
            isTara = true;
          }
        });
      });

    if (isTara) {
      this.leaveFor(bookingUrl);
    } else {
      this.leaveFor(this.configService.baseAuthUrl);
    }
  }

  loadTaraUserInfo$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.loadTaraUserInfo),
        switchMap(({ userId }) =>
          this._api
            .get(`/manage/accounts/tara-user-information/${userId}`)
            .pipe(map(n => n?.email)),
        ),
        tap(email => this._store.dispatch(RegisterActions.loadTaraUserInfoSuccess({ email }))),
        catchError(error => {
          this._store.dispatch(RegisterActions.loadTaraUserInfoFailed({ error }));
          this.redirect(error);

          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  taraResendEmailActivation$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.taraResendEmailActivation),
        switchMap(({ userId }) =>
          this._api.put(`/manage/accounts/tara-resend-invitation/${userId}`),
        ),
        tap(() => {
          this._store.dispatch(RegisterActions.taraResendEmailActivationSuccess());

          const title = this.translate(generalScopeKey, 'successTitle');
          const message = this.translate(translationKey, 'resendEMailActivationLinkSuccess');

          this._toastr.success(message, title);
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.taraResendEmailActivationFailed({ error }));
          this.redirect(error);

          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  activateUserAccount$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.activateUserAccount),
        switchMap(({ userId, accountType }) => {
          const isTelia: boolean = accountType == ELoginMethod.FI_TELIA;
          const authMethod: string = isTelia ? ELoginMethod.FI_TELIA : ELoginMethod.EE_TARA;
          return this._api.put(`/manage/accounts/${authMethod}-activate-user/${userId}`);
        }),
        tap(value => {
          this.navigate(AccountActivation.EMAIL_VERIFIED);
          this._store.dispatch(RegisterActions.activateTaraUserSuccess({ activated: true }));
        }),
        catchError(error => {
          this.navigate(AccountActivation.VERIFICATION_FAILED);
          this._store.dispatch(RegisterActions.activateTaraUserFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  updatePhoneNumber$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.updatePhoneNumber),
        switchMap(({ payload }) =>
          this._api.put(`/manage/accounts/change_phone`, payload).pipe(
            map(n => ({
              phone: payload?.phone,
              result: n,
            })),
          ),
        ),
        tap(({ phone }) => {
          this._store.dispatch(RegisterActions.updatePhoneNumberSuccess({ phone }));
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.updatePhoneNumberFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  updatePhoneNumberFailure$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.updatePhoneNumberFailed),
        tap(({ error }) => this._toastr.error(getErrorMessage(error))),
      );
    },
    { dispatch: false },
  );

  verifyPhoneNumber$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.verifyPhoneNumber),
        switchMap(({ payload }) => this._api.put(`/manage/accounts/verify_phone`, payload)),
        tap((success: boolean) => {
          this._store.dispatch(RegisterActions.verifyPhoneNumberSuccess({ success }));
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.verifyPhoneNumberFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  verifyPhoneNumberSuccess$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.verifyPhoneNumberSuccess),
        map(({ success }) => {
          if (success) {
            const m = this._transloco.translate(
              `${twoFactorAuthenticatorScopeKey}.mobileNumberVerified`,
            );
            this._toastr.success(m);
          } else {
            const h = this._transloco.translate(`${generalScopeKey}.errorOccuredTitle`);
            const m = this._transloco.translate(
              `${twoFactorAuthenticatorScopeKey}.invalidTokenMessage`,
            );
            this._toastr.error(m, h);
          }
        }),
      );
    },
    { dispatch: false },
  );

  enableMFA$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.enableMFA),
        switchMap(({ payload }) =>
          this._api.put(`/manage/accounts/set_two_factor_authentication`, payload).pipe(
            map(n => ({
              result: n,
              type: payload.twoFactorProvider,
            })),
          ),
        ),
        tap(r =>
          this._store.dispatch(
            RegisterActions.enableMFASuccess({
              data: r.result,
              authType: <any>r.type,
            }),
          ),
        ),
        catchError(error => {
          this._store.dispatch(RegisterActions.enableMFAFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  enableMFASuccess$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.enableMFASuccess),
        map(({ data, authType }) => {
          if (authType == EAuthenticationType.AUTHENTICATOR) {
            this._store.dispatch(RegisterActions.setAuthenticatorAppData({ data }));
          }
        }),
      );
    },
    { dispatch: false },
  );

  enableMFAVerify$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.enableMFAVerify),
        switchMap(({ payload }) =>
          this._api.put(`/manage/accounts/verify_two_factor_authentication`, payload).pipe(
            map((n: boolean) => ({
              result: n,
              type: payload.tokenProvider,
            })),
          ),
        ),
        tap(data => {
          console.log('enableMFAVerify$ data:', data);
          if ((data?.result as any)?.success === false) {
            const error = {
              error: {
                ErrorMessage: this._transloco.translate('twoFactorAuthenticator.invalidTokenMessage'),
              }
            }
            this._store.dispatch(RegisterActions.enableMFAVerifyFailed({ error }));
            return throwError(() => error);
          } else {
            this._store.dispatch(RegisterActions.enableMFAVerifySuccess({ data: <any>data }));
          }
        }),
        catchError(error => {
          this._store.dispatch(RegisterActions.enableMFAVerifyFailed({ error }));
          return throwError(() => error);
        }),
      );
    },
    { dispatch: false },
  );

  enableMFAVerifySuccess$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.enableMFAVerifySuccess),
        withLatestFrom(this.userId$, this.bookingUrl$),
        map(([{ data }, userid, bookingUrl]) => {
          if (data.result) {
            // show success message
            const m = this._transloco.translate(
              `${twoFactorAuthenticatorScopeKey}.twoFaSuccessText`,
            );
            this._toastr.success(m);
            // redirect to booking pt-ee user
            if (userid) {
              window.location.assign(withCampaignParams(bookingUrl, this.configService._envConfig));
            }
          } else {
            // show error message
            const h = this._transloco.translate(`${generalScopeKey}.errorOccuredTitle`);
            const m = this._transloco.translate(
              `${twoFactorAuthenticatorScopeKey}.invalidTokenMessage`,
            );

            this._toastr.error(m, h);
          }
        }),
      );
    },
    { dispatch: false },
  );

  enableMFAVerifyFailed$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(RegisterActions.enableMFAVerifyFailed),
        map(({ error }) => {
          const message = error?.error?.ErrorMessage;
          const title = this._transloco.translate(`${generalScopeKey}.errorOccuredTitle`);
          this._toastr.error(message, title);
        }),
      );
    },
    { dispatch: false },
  );

  getClaimsAsObject(claims: IClaim[]) {
    return claims.reduce((a, v) => ({ ...a, [v.claimType]: v.claimValue }), {});
  }

  translate(scope: string, key: string): string {
    return this._transloco.translate(`${scope}.${key}`);
  }

  redirect(e: HttpErrorResponse) {
    let activated: boolean = false;
    const message: string = e.error?.message || e.error?.ErrorMessage;
    if (message?.toLowerCase() == 'user is already activated') {
      activated = true;
    }

    if (activated) {
      return this.navigate(AccountActivation.EMAIL_ACTIVATED);
    }
    return this.navigate(AccountActivation.VERIFICATION_FAILED);
  }

  navigate(status: AccountActivation) {
    this._router.navigate(['registration', 'account_activation'], {
      queryParams: { status },
    });
  }
}
